import { of, forkJoin, throwError } from 'rxjs'
import { mergeMap, switchMap } from 'rxjs/operators'
import NagraPurchaseApi from 'services/nagra/PurchaseApi'
import UfinityPurchaseApi from 'services/ufinity/PurchaseApi'
import ConfigHelper from 'framework/helpers/config'

import { filterProductOffers } from '../factories'
import VoltError from 'VoltError'

/**
 * Deals with Playback requirements (session creation, tracking etc)
 */
export default class PurchaseApi extends UfinityPurchaseApi {
    constructor(config, otherApis = {}) {
        super(config, otherApis)
        this.nagraPurchaseApi = new NagraPurchaseApi(config, otherApis)
        this.gatewayApi = otherApis.gatewayApi
    }

    getSubscriptions(limit = 50) {
        const { fetchOnlySubscribedProducts = false } =
            ConfigHelper.getInstance().getConfig('nagra')
        /**
         * As Nagra does not support housekeeping of the products, get only subscribed products
         */
        return (
            fetchOnlySubscribedProducts
                ? this.nagraPurchaseApi.getPurchasedSubscriptions()
                : this.nagraPurchaseApi.getSubscriptions(limit)
        ).pipe(
            switchMap(({ isComplete, products }) =>
                of({ isComplete, products: filterProductOffers(products) })
            )
        )
    }

    getPurchasedTVODProducts(args) {
        return this.nagraPurchaseApi.getPurchasedTVODProducts(args)
    }

    getAllPurchasedTVODProducts(limit = 15) {
        return this.nagraPurchaseApi.getAllPurchasedTVODProducts(limit)
    }

    getProductsFromTitle(args) {
        // In theory the subscriptionIds are available only for SVOD but currently on the ifs proxy ingestion
        // the subscriptions ids are available also for TVODS giving us the capability to get the nagra product association for tvods
        // So for the moment, we can rely on this specific proxy parsing without adding additional call for getting nagra product from a TVOD ID
        const { subscriptionIds } = args

        const { useProxyProducts = true } = ConfigHelper.getInstance().getConfig('nagra')
        if (useProxyProducts) {
            return this.nagraPurchaseApi
                .getProductsFromTitle(args)
                .pipe(switchMap((products) => of(filterProductOffers(products))))
        }

        return forkJoin([
            // Retrieving the ufinity Offers
            this.gatewayApi.getUfinityOffersFromNagraProducts(subscriptionIds),
            // Retrieving the Nagra Product
            this.nagraPurchaseApi.getProductsFromTitle(args),
        ]).pipe(
            mergeMap(([UfinityOffers, NagraProducts]) =>
                of(
                    NagraProducts.map((product) => {
                        return product.update({
                            offers: UfinityOffers[product.id] || [],
                        })
                    })
                )
            ),
            switchMap((products) => of(filterProductOffers(products)))
        )
    }

    getTVODProductsData(productIds) {
        // Currently for this specific API, there is not need to retrieve the Ufinity offers as this method is called only
        // after a TVOD purchase for refreshing the entitlements. So we are same some backend loads for the moment
        return this.nagraPurchaseApi
            .getTVODProductsData(productIds)
            .pipe(switchMap((products) => of(filterProductOffers(products))))
    }

    getSubscriptionsData(productIds) {
        const { useProxyProducts = true } = ConfigHelper.getInstance().getConfig('nagra')
        if (useProxyProducts) {
            return this.nagraPurchaseApi
                .getSubscriptionsData(productIds)
                .pipe(switchMap((products) => of(filterProductOffers(products))))
        }

        return forkJoin([
            // Retrieving the ufinity Offers
            this.gatewayApi.getUfinityOffersFromNagraProducts(productIds),
            // Retrieving the Nagra Product
            this.nagraPurchaseApi.getSubscriptionsData(productIds),
        ]).pipe(
            mergeMap(([UfinityOffers, NagraProducts]) =>
                of(
                    NagraProducts.map((product) => {
                        return product.update({
                            offers: UfinityOffers[product.id] || [],
                        })
                    })
                )
            ),
            switchMap((products) => of(filterProductOffers(products)))
        )
    }

    /**
     * Redeem a voucher code
     *
     * @param {Object} options
     * @param {String} options.code value of voucherCode
     * @returns {Observable<Boolean>} returns boolean
     */
    redeemVoucherCode = ({ code }) => {
        return this.gatewayApi.redeemVoucherCode(code)
    }

    /**
     * get transaction list
     *
     * @param {Object} options
     * @param {String} options.fromDate start date of transaction list
     * @param {String} options.toDate end date of transaction list
     * @returns {Array<TransactionList>} returns transaction array
     */
    getTransactionList = ({ fromDate, toDate }) => {
        return this.gatewayApi.transactionList(fromDate, toDate)
    }

    /**
     * Add payment method for an account
     *
     * @param {Object} options
     * @param {string} options.paymentMethodId the payment method id got from a credit card iframe
     * @returns Observable
     */
    addPaymentMethod = ({ paymentMethodId }) =>
        throwError(new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE))

    /**
     * Delete payment method for an account
     *
     * @param {Object} options
     * @param {string} options.paymentMethodId the payment method id got from a credit card iframe
     * @returns Observable
     */
    deletePaymentMethod = ({ paymentMethodId }) =>
        throwError(new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE))

    sendPurchaseOTP = () => throwError(new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE))
    validatePurchaseOTP = () => throwError(new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE))
    getUserWallet = () => throwError(new VoltError(VoltError.codes.FEATURE_NOT_AVAILABLE))
}
